import { ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'

type SettingsRowProps = {
  /** Leave out for a row that is only content, such as a list or a note. */
  label?: ReactNode
  /** One sentence on what the setting does. */
  description?: ReactNode
  /** The id of the control, so clicking the label focuses it. */
  htmlFor?: string
  /**
   * `inline` puts the control on the right, for switches, selects and
   * buttons. `stacked` puts it under the label, for wide inputs like a
   * text area or a list.
   */
  layout?: 'inline' | 'stacked'
  /** The control. */
  children?: ReactNode
}

/**
 * One setting inside a `SettingsSection` card: a label and description on the
 * left, and its control on the right.
 */
export default function SettingsRow({
  label,
  description,
  htmlFor,
  layout = 'inline',
  children,
}: SettingsRowProps) {
  const text = label && (
    <Stack
      spacing={0.25}
      sx={{ flex: layout === 'inline' ? '1 1 280px' : undefined, minWidth: 0 }}
    >
      <Typography
        component={htmlFor ? 'label' : 'div'}
        htmlFor={htmlFor}
        sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 }}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.8125rem', lineHeight: 1.5, maxWidth: '60ch' }}
        >
          {description}
        </Typography>
      )}
    </Stack>
  )

  if (!label) return <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>

  if (layout === 'stacked') {
    return (
      <Stack spacing={1.5} sx={{ px: 2.5, py: 2 }}>
        {text}
        {children}
      </Stack>
    )
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      columnGap={3}
      rowGap={1.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 2.5, py: 2 }}
    >
      {text}
      {children && <Box sx={{ flexShrink: 0 }}>{children}</Box>}
    </Stack>
  )
}
